import { Chip, LinearProgress, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { addNewExam, archiveExam, deleteExam, getExam, publishExam, updateExam } from "../../api/ExamApi";
import { NewEntityId } from "../../data/constants";
import { ExamStateTranslationKey, ExamStatus, ExamViewModel, RequiredExam } from "../../model/Exam";
import { GetTranslation } from "../../translations/GetTranslation";
import DefaultButtons from "../common/buttons/DefaultButtons";
import DefaultPublishArchiveButtons from "../common/buttons/DefaultPublishArchiveButtons";
import TranslatedMessage from "../translations/TranslatedMessage";
import { initialExam, getInitialRequiredFieldsStatuses } from "./ExamConstants";
import ExamFormFields from "./ExamFormFields";

const ExamForm: FC = () => {
  const { examId } = useParams<{ examId?: string }>();
  const isCreating = examId === NewEntityId;

  const [isLoading, setIsLoading] = useState(!isCreating);
  const [exam, setExam] = useState<ExamViewModel>(initialExam);
  const [requiredFieldStatus, setRequiredFieldStatus] = useState(getInitialRequiredFieldsStatuses());

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const intl = useIntl();

  useEffect(() => {
    if (!isCreating && examId) {
      getExam(examId).then((result) => {
        setExam(result);
        setIsLoading(false);
      });
    }
  }, [isCreating, examId]);

  if (isLoading) {
    return (
      <div>
        <Typography variant="h5">
          <TranslatedMessage id={"exam.header"} />
        </Typography>
        <LinearProgress variant="query" />
      </div>
    );
  }

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    if (isCreating) {
      handleCreate();
    } else {
      handleUpdate();
    }
  };

  const handleCreate = () => {
    addNewExam(exam).then((response) => {
      if (response.success) {
        history.goBack();
      }
    });
  };

  const handleUpdate = () => {
    updateExam(exam).then((response) => {
      if (response.ok) {
        history.goBack();
      }
    });
  };

  const handlePublish = () => {
    publishExam(exam).then((response) => {
      if (response.ok) {
        history.goBack();
        enqueueSnackbar(<TranslatedMessage id="exam.snackbar.successfulPublish" />);
      }
    });
  };

  const handleDelete = () => {
    deleteExam(exam.id).then((response) => {
      if (response.ok) {
        history.goBack();
        enqueueSnackbar(<TranslatedMessage id="exam.snackbar.successfulDelete" />);
      }
    });
  };

  const handleArchive = () => {
    archiveExam(exam).then((response) => {
      if (response.ok) {
        history.goBack();
        enqueueSnackbar(<TranslatedMessage id="exam.snackbar.successfulArchive" />);
      }
    });
  };

  const validateForm = () => {
    let formHasEmptyFields = false;

    const isFieldEmpty = (value: string) => {
      const isEmpty = value === undefined || value === null || value === "";
      formHasEmptyFields = formHasEmptyFields || isEmpty;
      return isEmpty;
    };

    const currentStatus: RequiredExam = {
      isNameDEEmpty: isFieldEmpty(exam.nameDE),
      isNameENEmpty: isFieldEmpty(exam.nameEN),
      isNameFREmpty: isFieldEmpty(exam.nameFR),
      isNameITEmpty: isFieldEmpty(exam.nameIT),
    };

    setRequiredFieldStatus(currentStatus);

    return !formHasEmptyFields;
  };

  return (
    <>
      <Typography variant="h5">
        <TranslatedMessage id={"exam.header"} />
      </Typography>

      <DefaultButtons
        additionalButtons={
          <>
            <Chip label={GetTranslation(intl, ExamStateTranslationKey[exam.status])} />
            <DefaultPublishArchiveButtons
              isArchiveButtonVisible={exam.status === ExamStatus.Published}
              isPublishButtonVisible={exam.status === ExamStatus.Draft && !isCreating}
              onPublish={handlePublish}
              onArchive={handleArchive}
            />
          </>
        }
        onClose={() => history.goBack()}
        onDelete={handleDelete}
        onSave={handleSave}
        isDeleteButtonVisible={exam.status === ExamStatus.Draft && !isCreating}
        isSaveButtonVisible={exam.status === ExamStatus.Draft}
      />

      <ExamFormFields
        exam={exam}
        handleFieldChange={(change: Partial<ExamViewModel>) => setExam({ ...exam, ...change })}
        requiredFieldStatus={requiredFieldStatus}
        isReadOnly={exam.status !== ExamStatus.Draft}
      />
    </>
  );
};

export default ExamForm;
